import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Toggle, Dropdown, IconColor, IconName, Tooltip } from '@ets/design-system';

type PriorityProps = {
  priority: Card.Priority;
  onChange: (priority: Card.Priority) => void;
  disabled?: boolean;
  testId: string;
  hasPermissions?: boolean;
};

const PRIORITY: {
  [key in Card.Priority]: { icon: IconName; color: IconColor; labelId: string };
} = {
  High: {
    icon: 'chevron-up',
    color: 'error',
    labelId: 'editor.sidebar.review.filter.priority.high',
  },
  Medium: {
    icon: 'equals',
    color: 'warning',
    labelId: 'editor.sidebar.review.filter.priority.medium',
  },
  Low: {
    icon: 'chevron-down',
    color: 'success',
    labelId: 'editor.sidebar.review.filter.priority.low',
  },
};

const Priority = ({ priority, onChange, disabled, testId, hasPermissions }: PriorityProps) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const handleChangePriority = (newPriority: typeof priority) => {
    if (priority !== newPriority) {
      onChange(newPriority);
    }
  };

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger>
        <Tooltip
          placement="top"
          content={intl.formatMessage({
            id: hasPermissions ? 'global.priority' : 'CHANGE_PRIORITY_CARD',
          })}
          testId={`${testId}-priority`}
          visible={!disabled}
        >
          <span>
            <Toggle
              variant="link"
              size="m"
              iconProps={{
                icon: PRIORITY[priority]?.icon,
                color: PRIORITY[priority]?.color,
              }}
              isToggled={isOpen}
              testId={`${testId}-priority`}
              disabled={disabled}
            />
          </span>
        </Tooltip>
      </Dropdown.Trigger>
      <Dropdown.Content align="end" testId={`${testId}-priority-dropdown`}>
        {(Object.keys(PRIORITY) as Card.Priority[]).map((priority) => (
          <Dropdown.Item
            key={priority}
            onClick={() => handleChangePriority(priority)}
            disabled={disabled || !hasPermissions}
            prefix={PRIORITY[priority].icon}
            prefixColor={PRIORITY[priority].color}
            testId={`${testId}-priority-${priority}-item`}
          >
            <FormattedMessage id={PRIORITY[priority].labelId} />
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};

export default Priority;
